<template>
  <div class="flex min-h-0 flex-col overflow-hidden" :style="{ maxHeight: maxHeightStyle }">
    <div v-if="breadcrumbs.length > 0" class="mb-1 border-b border-gray-20 px-1 pb-1">
      <button
        data-testid="multilevel-select-back"
        type="button"
        class="flex w-full items-center gap-2 rounded-sm border-none bg-transparent px-3 py-2 text-left text-sm text-gray-70 transition-colors duration-150 ease-in hover:bg-active-secondary"
        @click="goBack"
        @keydown.enter.stop.prevent="goBack"
        @keydown.space.stop.prevent="goBack"
        @keydown.up.stop.prevent="focusLastOption"
        @keydown.down.stop.prevent="focusFirstOption"
        @keydown.home.stop.prevent="focusFirstOption"
        @keydown.end.stop.prevent="focusLastOption"
        @keydown.esc.stop.prevent="handleEscape"
      >
        <arrow-left-icon class="h-4 w-4" />
        {{ breadcrumbLabel }}
      </button>
    </div>

    <div
      ref="dropdownRef"
      data-testid="multilevel-select-dropdown"
      class="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto pb-1"
      role="menu"
    >
      <template v-if="currentOptions.length > 0">
        <button
          v-for="option in currentOptions"
          :key="option.id"
          data-testid="multilevel-select-option"
          data-option
          type="button"
          class="mx-1 flex items-center justify-between rounded-sm border-none bg-transparent px-3 py-2 text-left text-sm text-gray-80 transition-colors duration-150 ease-in hover:bg-active-secondary"
          :class="{ 'bg-active-secondary font-medium': isSelected(option) }"
          role="menuitem"
          @click="handleOptionClick(option)"
          @keydown.enter.stop.prevent="handleOptionClick(option)"
          @keydown.space.stop.prevent="handleOptionClick(option)"
          @keydown.up.stop.prevent="focusPreviousOption"
          @keydown.down.stop.prevent="focusNextOption"
          @keydown.home.stop.prevent="focusFirstOption"
          @keydown.end.stop.prevent="focusLastOption"
          @keydown.esc.stop.prevent="handleEscape"
        >
          <span class="flex-1 break-all">{{ option.name }}</span>
          <chevron-right-icon v-if="hasChildren(option)" class="h-4 w-4 shrink-0 text-gray-50" />
          <check-icon v-if="isSelected(option)" class="h-4 w-4 shrink-0 text-active-primary" />
        </button>
      </template>
      <div v-else class="px-4 py-3 text-sm text-gray-50">
        {{ emptyLabel }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'
import { ChevronRightIcon, ArrowLeftIcon, CheckIcon } from '@klausapp/ui-kit/icons'
import type { MultiLevelOption } from '../types'

interface Props {
  modelValue?: string | null
  options: MultiLevelOption[]
  rootLabel?: string
  emptyLabel?: string
  maxHeight?: number | string
}

interface Emits {
  (e: 'update:modelValue', value: string | null): void
  (e: 'select', value: string): void
  (e: 'escape'): void
  (e: 'navigation-change', value: { depth: number; isNested: boolean }): void
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  rootLabel: '',
  emptyLabel: '',
  maxHeight: undefined,
})

const emit = defineEmits<Emits>()

const breadcrumbs = ref<MultiLevelOption[]>([])
const dropdownRef = ref<HTMLDivElement | null>(null)

const currentOptions = computed(() => breadcrumbs.value.at(-1)?.children ?? props.options)

const maxHeightStyle = computed(() => {
  if (props.maxHeight === undefined) return undefined
  return typeof props.maxHeight === 'number' ? `${props.maxHeight}px` : props.maxHeight
})

const breadcrumbLabel = computed(() => {
  if (breadcrumbs.value.length === 0) return ''
  return breadcrumbs.value.length === 1
    ? props.rootLabel || breadcrumbs.value[0].name
    : breadcrumbs.value.at(-2)?.name || ''
})

const getOptionElements = () =>
  Array.from(dropdownRef.value?.querySelectorAll<HTMLButtonElement>('[data-option]') ?? [])

const focusFirstOption = () => getOptionElements().at(0)?.focus()
const focusLastOption = () => getOptionElements().at(-1)?.focus()

const focusOptionByOffset = (currentOption: EventTarget | null, offset: number) => {
  if (!(currentOption instanceof HTMLButtonElement)) return

  const optionElements = getOptionElements()
  const currentIndex = optionElements.indexOf(currentOption)

  if (currentIndex === -1) return

  const nextIndex = currentIndex + offset
  optionElements[nextIndex]?.focus()
}

const focusPreviousOption = (event: KeyboardEvent) => {
  focusOptionByOffset(event.currentTarget, -1)
}

const focusNextOption = (event: KeyboardEvent) => {
  focusOptionByOffset(event.currentTarget, 1)
}

const hasChildren = (option: MultiLevelOption) => !!option.children?.length

const isSelected = (option: MultiLevelOption) => props.modelValue === option.id

const emitNavigationChange = () => {
  emit('navigation-change', {
    depth: breadcrumbs.value.length,
    isNested: breadcrumbs.value.length > 0,
  })
}

const handleOptionClick = async (option: MultiLevelOption) => {
  if (hasChildren(option)) {
    breadcrumbs.value.push(option)
    emitNavigationChange()
    await nextTick()
    focusFirstOption()
    return
  }

  emit('update:modelValue', option.id)
  emit('select', option.id)
}

const goBack = async () => {
  breadcrumbs.value.pop()
  emitNavigationChange()
  await nextTick()
  focusFirstOption()
}

const handleEscape = () => {
  emit('escape')
}

const reset = () => {
  breadcrumbs.value = []
  emitNavigationChange()
}

defineExpose({
  focusFirstOption,
  reset,
})
</script>
